(() => {
if (customElements.get('slider-component')) return;

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]') || this.querySelector('[js-cart-recommendations="page"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.sliderButtons = this.querySelector('.slider-buttons');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.sliderDots = this.querySelector('.slider-dots');
    this.isProductMediaGallery = this.classList.contains('product-media-gallery');
    this.currentPage = 1;

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this), { passive: true });
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    // Initialize video event listeners
    if (this.isProductMediaGallery) {
      this.initVideoEvents();
    }
  }

  // Initialize video play and pause button event listeners
  initVideoEvents() {
    this.querySelectorAll('.product__media-list .deferred-media__poster').forEach((playButton) => {
      playButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const deferredMedia = playButton.closest('deferred-media');
        if (deferredMedia) {

          this.querySelectorAll('.deferred-media__pause-button').forEach((pauseButton) => {
            pauseButton.setAttribute('tabindex', '-1');
          });

          this.querySelectorAll('.deferred-media.video--playing').forEach((playingMedia) => {
            if (playingMedia !== deferredMedia) {
              playingMedia.classList.add('video--paused');
              playingMedia.classList.remove('video--playing');
            }
          });
          deferredMedia.classList.add('video--playing');
          deferredMedia.classList.remove('video--paused');

          if (deferredMedia.hasAttribute('loaded')) {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.loop = true;
              video.muted = true;
              video.play().catch(err => console.error('Video play failed:', err));
            }
          }

          const pauseButton = deferredMedia.querySelector('.deferred-media__pause-button');
          if (pauseButton) {
            pauseButton.setAttribute('tabindex', '0');
          }
        }
      });
    });

    // Handle pause button clicks
    this.querySelectorAll('.product__media-list .deferred-media__pause-button').forEach((pauseButton) => {
      pauseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const deferredMedia = pauseButton.closest('deferred-media');
        if (deferredMedia) {
          const video = deferredMedia.querySelector('video');

          if (video) {
            video.pause();
            deferredMedia.classList.remove('video--playing');
            deferredMedia.classList.add('video--paused');
          }
          pauseButton.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.currentPage = 1;
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );

    if (this.isProductMediaGallery) {
      // For product-media-gallery one dot per slide
      this.totalPages = this.sliderItemsToShow.length;
    } else {
      // Other sliders -> original logic
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    }

    this.update();
    this.buildSliderDots();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
    if (!this.slider) return;
    this.slider.scrollTo(0, 0);
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const newPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.isProductMediaGallery === false && this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.currentPage = this.totalPages;
    }
    else {
      this.currentPage = newPage > 0 ? newPage : 1;
    }

    // For product-media-gallery only
    if (this.isProductMediaGallery) {
      this.sliderItemsToShow.forEach((item, index) => {
        if (index + 1 === this.currentPage) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }
      });
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;

      if (this.sliderDots && this.sliderDots.querySelectorAll('.slider-dot').length) {
        this.updateSliderDots(this.currentPage);
      }
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    // For product-media-gallery disable prev button only on the first page
    if (this.isProductMediaGallery) {
      if (this.currentPage === 1) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
      if (this.currentPage === this.totalPages) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    } else {
      // Original logic for other sliders
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
        this.sliderButtons?.classList.add('slider-button-prev-disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
        this.sliderButtons?.classList.remove('slider-button-prev-disabled');
      }
      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
        this.sliderButtons?.classList.add('slider-button-next-disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
        this.sliderButtons?.classList.remove('slider-button-next-disabled');
      }
    }

    if (this.closest(".quote-section")) {
      this.checkQuoteSection();
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  buildSliderDots() {
    if (this.sliderDots === null) return;

    this.sliderDots.textContent = '';

    // Create a variable to store the divs
    const dotsContainer = document.createDocumentFragment();

    // Adjust dots for mobile for shop the look section
    if (this.closest(".shop-the-look")) {
      this.checkShopInLook();
    }

    // Loop through totalPages and create a div for each item
    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      const dotSpan = document.createElement('span');
      const slideNumber = i + 1;
      dot.className = 'slider-dot link';
      dotSpan.className = 'dot';
      dot.ariaLabel = 'Slide ' + slideNumber + ' of ' + this.totalPages; // Optional: Add text content to the dot
      dot.dataset.step = i;

      if (this.currentPage === slideNumber) {
        dot.classList.add('slider-dot--active');
        dot.setAttribute('aria-current', true);
      }
      dot.appendChild(dotSpan);
      dotsContainer.appendChild(dot);
    }

    // Append the dotsContainer to sliderDots
    this.sliderDots.appendChild(dotsContainer);

    // Add event listeners to each dot
    this.addDotEventListeners(this.sliderDots);
  }

  checkShopInLook() {
    // Adjust dots for mobile for shop the look section
    if (this.closest(".shop-the-look")) {
      const mobileColumns = parseInt(this.dataset.columnsMobile || 2); // Default to 2 columns
      if (window.innerWidth <= 989) {
        if (mobileColumns === 2) {
          this.totalPages = Math.max(this.totalPages - 1, 1); // Reduce dots by 2
        }
      }
    }
  }

  checkQuoteSection() {
    // Adjust next button for quote section
    if (!this.closest(".quote-section")) return;

    const quoteCarousels = this.querySelector(".quote-carousel");
    if (quoteCarousels.length < 2) {
      return;
    }
    const blockSize = Number(quoteCarousels.getAttribute("data-blocksize"));
    if (this.currentPage === blockSize) {
      this.querySelector(".slider-button--next").setAttribute('disabled', 'disabled');
    } else {
      this.querySelector(".slider-button--next").removeAttribute("disabled");
    }
  }

  addDotEventListeners(dots) {
    dots.querySelectorAll('.slider-dot').forEach((dot) => {
      dot.addEventListener('click', this.onDotClick.bind(this));
    });
  }

  onDotClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const step = parseInt(event.currentTarget.dataset.step);
    this.slideScrollPosition = step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  updateSliderDots(currentPage) {
    const sliderDots = this.sliderDots.querySelectorAll('.slider-dot');

    if (!sliderDots.length || currentPage < 1 || currentPage > sliderDots.length) {
      this.currentPage = 1;
      currentPage = 1;
    }

    sliderDots.forEach((dot) => {
      dot.classList.remove('slider-dot--active');
      dot.removeAttribute('aria-current');
    });

    sliderDots[currentPage - 1].classList.add('slider-dot--active');
    sliderDots[currentPage - 1].setAttribute('aria-current', true);
  }
}

window.SliderComponent = SliderComponent;
customElements.define('slider-component', SliderComponent);
})();
